import { Link, useLocation, useNavigate } from 'react-router-dom';
import homeIcon from '../assets/icons/home.svg';

// "Back" + "Dashboard" row at the top of every teacher and parent screen, so
// nobody has to rely on the browser's Back button (or get stuck on a page
// reached from a link or a bookmark).
//
// Back goes to the previous screen *inside the app* when there is one (same
// rule as BackLink: React Router keys the first entry of a session
// "default"). When the page was opened directly -- an email link, a
// bookmark, a fresh sign-in -- the "previous screen" would be another site
// or the sign-in page, so Back goes to `backFallbackTo` (the page's
// natural parent, e.g. "Your students" for a planning step) or, failing
// that, the dashboard. Hidden on the dashboard itself.

type Props = {
  dashboardHref: string;
  backFallbackTo?: string | undefined;
};

export function ScreenNav({ dashboardHref, backFallbackTo }: Props) {
  const navigate = useNavigate();
  const location = useLocation();
  if (location.pathname === dashboardHref) return null;
  const hasPreviousPage = location.key !== 'default';

  return (
    <nav
      aria-label="Page navigation"
      className="mb-5 flex flex-wrap items-center gap-2 print:hidden"
    >
      <button
        type="button"
        onClick={() => {
          if (hasPreviousPage) {
            void navigate(-1);
          } else {
            void navigate(backFallbackTo ?? dashboardHref);
          }
        }}
        className="inline-flex min-h-9 items-center gap-1.5 rounded-full border border-line bg-white px-3.5 text-sm font-semibold text-forest-800 transition-colors hover:border-forest-800 hover:bg-sage"
      >
        <span aria-hidden="true">←</span> Back
      </button>
      <Link
        to={dashboardHref}
        className="inline-flex min-h-9 items-center gap-1.5 rounded-full px-3 text-sm font-semibold text-muted transition-colors hover:bg-white hover:text-forest-800"
      >
        <img src={homeIcon} alt="" className="size-4" />
        Dashboard
      </Link>
    </nav>
  );
}
